import { useCallback, useState } from 'react';

/**
 * DOM 요소에 대한 안정적인 ref 관리를 위한 훅
 *
 * @description
 * React의 기본 useRef는 동적 렌더링 상황에서 ref.current가 null이 되는 문제가 있습니다.
 * 이 훅은 useState와 useCallback을 조합하여 더 안정적인 ref 관리를 제공합니다.
 *
 * @example
 * ```tsx
 * const { elementRef, setElementRef } = useElementRef<HTMLDivElement>();
 *
 * return (
 *   <div>
 *     <SomeComponent ref={setElementRef} />
 *     <Modal triggerEl={elementRef} />
 *   </div>
 * );
 * ```
 *
 * @template T - DOM 요소의 타입
 * @returns {object} elementRef와 setElementRef 함수
 */
export const useElementRef = <T extends HTMLElement = HTMLElement>() => {
  const [elementRef, setElementRef] = useState<T | null>(null);

  const refCallback = useCallback((element: T | null) => {
    setElementRef(element);
  }, []);

  return {
    /**
     * 현재 DOM 요소에 대한 참조
     */
    elementRef,
    /**
     * ref 콜백 함수 - React 컴포넌트의 ref prop에 전달
     */
    setElementRef: refCallback,
  };
};
